/**
 * AI System List Component
 * Displays all AI systems with filtering and CRUD operations
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Typography,
  Grid,
  TextField,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  FileDownload as ExportIcon,
  FileUpload as ImportIcon,
  ContentCopy as CloneIcon,
  FindReplace as FindReplaceIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { accountApi, aiSystemApi, symbolApi } from '../../services/api';
import type { Account, DataWarehouseSymbol } from '../../services/api';
import type {
  AISystemListItem,
  AISystem,
  AISystemReplacementRule,
  AISystemScopedReplacementRule,
} from '../../types';
import { PageHeader, LoadingSpinner, ConfirmDialog } from '../common';
import { AISystemFormAdvanced } from './AISystemFormAdvanced';
import { CreateSystemWizard } from './CreateSystemWizard';
import { useAuth } from '../../context/AuthContext';

type SystemStatusFilter = 'all' | 'active' | 'inactive';
type SystemTemplateFilter = 'all' | 'systems' | 'templates';
type SystemSortOrder = '-created_at' | 'created_at' | '-week_cost' | 'week_cost';
type ToggleSystemStatePayload = {
  id: number;
  isActive: boolean;
  name: string;
};

type ReplacementRow = AISystemReplacementRule & { id: number };

type AccountReplacementForm = {
  newAccountId: string;
  oldAccountId: string;
};

type SymbolReplacementForm = {
  newSymbol: string;
  oldSymbol: string;
};

let replacementRowCounter = 0;

const createReplacementRow = (): ReplacementRow => ({
  id: ++replacementRowCounter,
  from: '',
  to: '',
  case_sensitive: true,
});

const toReplacementRules = (rows: ReplacementRow[]): AISystemReplacementRule[] =>
  rows
    .filter((row) => row.from.trim().length > 0)
    .map(({ from, to, case_sensitive }) => ({
      from: from.trim(),
      to,
      case_sensitive,
    }));

const emptyAccountReplacement = (): AccountReplacementForm => ({
  newAccountId: '',
  oldAccountId: '',
});

const emptySymbolReplacement = (): SymbolReplacementForm => ({
  newSymbol: '',
  oldSymbol: '',
});

const formatCreatedDate = (value: string) =>
  new Date(value).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

const formatDateTime = (value?: string | null) => {
  if (!value) return 'Not scheduled';
  return new Date(value).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatCurrency = (value?: number | null) =>
  `$${(value ?? 0).toFixed(2)}`;

const formatRunStatus = (status?: string | null) => {
  if (!status) {
    return 'No runs yet';
  }

  switch (status) {
    case 'completed':
      return 'Completed';
    case 'running':
      return 'Running';
    case 'error':
      return 'Error';
    case 'start':
      return 'Starting';
    case 'stopped':
      return 'Stopped';
    default:
      return status;
  }
};

const runStatusColor = (status?: string | null): 'success' | 'info' | 'error' | 'default' => {
  switch (status) {
    case 'completed':
      return 'success';
    case 'running':
    case 'start':
      return 'info';
    case 'error':
      return 'error';
    default:
      return 'default';
  }
};

export const AISystemList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<SystemStatusFilter>('all');
  const [templateFilter, setTemplateFilter] = useState<SystemTemplateFilter>('all');
  const [sortOrder, setSortOrder] = useState<SystemSortOrder>('-created_at');
  const [formOpen, setFormOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editingSystem, setEditingSystem] = useState<AISystem | undefined>();
  const [deletingSystem, setDeletingSystem] = useState<AISystemListItem | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importData, setImportData] = useState<string>('');
  const [apiKeyMappings, setApiKeyMappings] = useState<Record<string, string>>({});
  const [importReplacementRows, setImportReplacementRows] = useState<ReplacementRow[]>(() => [createReplacementRow()]);
  const [bulkReplaceDialogOpen, setBulkReplaceDialogOpen] = useState(false);
  const [bulkReplaceSystem, setBulkReplaceSystem] = useState<AISystemListItem | null>(null);
  const [bulkReplacementRows, setBulkReplacementRows] = useState<ReplacementRow[]>(() => [createReplacementRow()]);
  const [bulkReplaceIncludeAgents, setBulkReplaceIncludeAgents] = useState(true);
  const [accountReplacement, setAccountReplacement] = useState<AccountReplacementForm>(() => emptyAccountReplacement());
  const [symbolReplacement, setSymbolReplacement] = useState<SymbolReplacementForm>(() => emptySymbolReplacement());
  const [symbolSearch, setSymbolSearch] = useState('');

  useEffect(() => {
    if (searchParams.get('create') === '1') {
      setWizardOpen(true);
    }
  }, [searchParams]);

  const closeCreateWizard = () => {
    setWizardOpen(false);

    if (searchParams.get('create') !== '1') {
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('create');
    setSearchParams(nextParams, { replace: true });
  };

  const systemFilters = useMemo(
    () => ({
      search: searchTerm || undefined,
      is_active:
        statusFilter === 'all'
          ? undefined
          : statusFilter === 'active',
      is_template:
        templateFilter === 'all'
          ? undefined
          : templateFilter === 'templates',
      ordering: sortOrder,
    }),
    [searchTerm, sortOrder, statusFilter, templateFilter],
  );

  // Fetch systems
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['systems', systemFilters],
    queryFn: () => aiSystemApi.list(systemFilters),
    refetchOnMount: 'always',
    staleTime: 0,
  });

  const accountsQuery = useQuery({
    queryKey: ['accounts'],
    queryFn: accountApi.list,
    enabled: bulkReplaceDialogOpen,
    staleTime: 60_000,
  });

  const symbolsQuery = useQuery({
    queryKey: ['data-warehouse-symbols', symbolSearch.trim()],
    queryFn: () => symbolApi.list({ search: symbolSearch.trim() || undefined, page_size: 50 }),
    enabled: bulkReplaceDialogOpen,
    staleTime: 30_000,
  });

  const accountOptions = accountsQuery.data || [];
  const symbolOptions = symbolsQuery.data || [];
  const selectedSymbolOption = symbolOptions.find(
    (option) => option.symbol === symbolReplacement.newSymbol,
  );
  const selectedOldAccountOptionId = accountOptions.some(
    (account) => String(account.id) === String(accountReplacement.oldAccountId),
  )
    ? accountReplacement.oldAccountId
    : '';

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: ToggleSystemStatePayload) =>
      aiSystemApi.update(id, { is_active: isActive }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['systems'] });
      toast.success(`${variables.name} ${variables.isActive ? 'activated' : 'deactivated'}`);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update system status');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => aiSystemApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['systems'] });
      queryClient.invalidateQueries({ queryKey: ['experts'] });
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      toast.success('System deleted successfully');
      setDeletingSystem(null);
    },
    onError: (error: any) => {
      console.error('Failed to delete system:', error);
      
      // If it's a 404, the system was already deleted - refresh the list
      if (error.response?.status === 404) {
        console.log('System already deleted, refreshing list...');
        queryClient.invalidateQueries({ queryKey: ['systems'] });
        queryClient.invalidateQueries({ queryKey: ['experts'] });
        queryClient.invalidateQueries({ queryKey: ['schedules'] });
        toast.info('System was already deleted. List refreshed.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to delete system');
      }
      
      setDeletingSystem(null);
    },
  });

  // Export system
  const handleExport = async (system: AISystemListItem) => {
    try {
      const exportData = await aiSystemApi.export(system.id, { mask_api_keys: true });
      
      // Create a downloadable JSON file
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { 
        type: 'application/json' 
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ai-system-${system.name.replace(/\s+/g, '-').toLowerCase()}-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('System exported successfully');
    } catch (error) {
      toast.error('Failed to export system');
      console.error('Export error:', error);
    }
  };

  // Import mutation
  const importMutation = useMutation({
    mutationFn: ({ data, mappings, replacements }: { data: Record<string, any>; mappings: Record<string, string>; replacements: AISystemReplacementRule[] }) =>
      aiSystemApi.importSystem(data, mappings, replacements),
    onSuccess: async (response) => {
      // Clear search term to show all systems including the newly imported one
      setSearchTerm('');
      // Invalidate all systems queries
      queryClient.invalidateQueries({ queryKey: ['systems'] });
      // Force immediate refetch with empty search
      await refetch();
      toast.success('System imported successfully');
      const total = response.replacement_summary?.total_replacements ?? 0;
      if (total > 0) {
        toast.info(`${total} replacement${total === 1 ? '' : 's'} applied during import`);
      }
      setImportDialogOpen(false);
      setImportData('');
      setApiKeyMappings({});
      setImportReplacementRows([createReplacementRow()]);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to import system');
    },
  });

  // Clone mutation
  const cloneMutation = useMutation({
    mutationFn: (id: number) => aiSystemApi.clone(id),
    onSuccess: async (response) => {
      queryClient.invalidateQueries({ queryKey: ['systems'] });
      await refetch();
      toast.success(response.message || 'System cloned successfully');
      const removedAccounts = response.account_reference_summary?.total_replacements ?? 0;
      if (removedAccounts > 0) {
        toast.info('Template account IDs were cleared. Choose target account(s) before running.');
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to clone system');
    },
  });

  const resetBulkReplacementForm = () => {
    setBulkReplacementRows([createReplacementRow()]);
    setBulkReplaceIncludeAgents(true);
    setAccountReplacement(emptyAccountReplacement());
    setSymbolReplacement(emptySymbolReplacement());
    setSymbolSearch('');
  };

  const bulkReplaceMutation = useMutation({
    mutationFn: ({
      systemId,
      replacements,
      scopedReplacements,
      includeAgents,
    }: {
      systemId: number;
      replacements: AISystemReplacementRule[];
      scopedReplacements: AISystemScopedReplacementRule[];
      includeAgents: boolean;
    }) => aiSystemApi.bulkReplace(systemId, replacements, {
      include_agents: includeAgents,
      scoped_replacements: scopedReplacements,
    }),
    onSuccess: async (response) => {
      queryClient.invalidateQueries({ queryKey: ['systems'] });
      queryClient.invalidateQueries({ queryKey: ['ai-system', String(response.ai_system.id)] });
      await refetch();
      const total = response.replacement_summary?.total_replacements ?? 0;
      toast.success(`${total} replacement${total === 1 ? '' : 's'} applied`);
      setBulkReplaceDialogOpen(false);
      setBulkReplaceSystem(null);
      resetBulkReplacementForm();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to apply replacements');
    },
  });

  // Handle import
  const handleImport = async () => {
    let data: Record<string, any>;
    try {
      data = JSON.parse(importData);
    } catch (error) {
      toast.error('Invalid JSON format');
      return;
    }

    try {
      await importMutation.mutateAsync({
        data,
        mappings: apiKeyMappings,
        replacements: toReplacementRules(importReplacementRows),
      });
    } catch (error) {
      // onError handles API failures.
    }
  };

  const toScopedReplacementRules = (): AISystemScopedReplacementRule[] => {
    const rules: AISystemScopedReplacementRule[] = [];
    const newAccountId = accountReplacement.newAccountId.trim();
    const oldAccountId = accountReplacement.oldAccountId.trim();
    const newSymbol = symbolReplacement.newSymbol.trim().toUpperCase();
    const oldSymbol = symbolReplacement.oldSymbol.trim();

    if (newAccountId) {
      rules.push({
        field: 'account',
        new: newAccountId,
        old: oldAccountId || undefined,
      });
    }

    if (newSymbol) {
      rules.push({
        field: 'symbol',
        new: newSymbol,
        old: oldSymbol || undefined,
        case_sensitive: false,
      });
    }

    return rules;
  };

  const handleBulkReplace = async () => {
    if (!bulkReplaceSystem) return;
    const replacements = toReplacementRules(bulkReplacementRows);
    const scopedReplacements = toScopedReplacementRules();
    if (replacements.length === 0 && scopedReplacements.length === 0) {
      toast.error('Add at least one replacement');
      return;
    }
    try {
      await bulkReplaceMutation.mutateAsync({
        systemId: bulkReplaceSystem.id,
        replacements,
        scopedReplacements,
        includeAgents: bulkReplaceIncludeAgents,
      });
    } catch (error) {
      // onError handles API failures.
    }
  };

  // Handle clone
  const handleClone = async (system: AISystemListItem) => {
    const action = system.is_template ? 'Use template' : 'Clone';
    if (window.confirm(`${action} "${system.name}"? This will create a copy with all experts and connections.`)) {
      await cloneMutation.mutateAsync(system.id);
    }
  };

  const handleToggleActive = (system: AISystemListItem, isActive: boolean) => {
    toggleActiveMutation.mutate({ id: system.id, isActive, name: system.name });
  };

  const handleOpenBulkReplace = (system: AISystemListItem) => {
    setBulkReplaceSystem(system);
    resetBulkReplacementForm();
    setBulkReplaceDialogOpen(true);
  };

  const updateReplacementRow = (
    setter: React.Dispatch<React.SetStateAction<ReplacementRow[]>>,
    id: number,
    patch: Partial<ReplacementRow>,
  ) => {
    setter((rows) => rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const removeReplacementRow = (
    setter: React.Dispatch<React.SetStateAction<ReplacementRow[]>>,
    id: number,
  ) => {
    setter((rows) => (rows.length <= 1 ? [createReplacementRow()] : rows.filter((row) => row.id !== id)));
  };

  const formatAccountLabel = (account: Account) => {
    const pieces = [account.name || account.id, account.platform, account.simple_id ? `#${account.simple_id}` : ''];
    return pieces.filter(Boolean).join(' / ');
  };

  const formatSymbolLabel = (option: DataWarehouseSymbol | string) => {
    if (typeof option === 'string') return option;
    const details = [option.name, option.exchange, option.asset_class].filter(Boolean).join(' / ');
    return details ? `${option.symbol} - ${details}` : option.symbol;
  };

  const renderScopedReplacementControls = () => (
    <Stack spacing={1.5}>
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
        <Typography variant="subtitle2" gutterBottom>
          Replace account
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
          <FormControl
            size="small"
            fullWidth
            disabled={bulkReplaceMutation.isPending || accountsQuery.isLoading}
            sx={{ flex: 1, minWidth: { sm: 220 } }}
          >
            <InputLabel>New account</InputLabel>
            <Select
              label="New account"
              value={accountReplacement.newAccountId}
              onChange={(event) => setAccountReplacement((current) => ({
                ...current,
                newAccountId: event.target.value,
              }))}
            >
              {accountOptions.map((account) => (
                <MenuItem key={account.id} value={account.id}>
                  {formatAccountLabel(account)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl
            size="small"
            fullWidth
            disabled={bulkReplaceMutation.isPending || accountsQuery.isLoading}
            sx={{ minWidth: { sm: 220 }, width: { sm: 240 } }}
          >
            <InputLabel>Old account</InputLabel>
            <Select
              label="Old account"
              value={selectedOldAccountOptionId}
              onChange={(event) => setAccountReplacement((current) => ({
                ...current,
                oldAccountId: event.target.value,
              }))}
            >
              <MenuItem value="">
                <em>Choose old account</em>
              </MenuItem>
              {accountOptions.map((account) => (
                <MenuItem key={account.id} value={account.id}>
                  {formatAccountLabel(account)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Old account ID (optional)"
            size="small"
            value={accountReplacement.oldAccountId}
            disabled={bulkReplaceMutation.isPending}
            onChange={(event) => setAccountReplacement((current) => ({
              ...current,
              oldAccountId: event.target.value,
            }))}
            sx={{ minWidth: { sm: 240 } }}
          />
        </Stack>
      </Box>

      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
        <Typography variant="subtitle2" gutterBottom>
          Replace symbol
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
          <Autocomplete<DataWarehouseSymbol | string, false, false, true>
            freeSolo
            size="small"
            fullWidth
            options={symbolOptions}
            loading={symbolsQuery.isFetching}
            value={symbolReplacement.newSymbol ? selectedSymbolOption || symbolReplacement.newSymbol : null}
            inputValue={symbolSearch}
            disabled={bulkReplaceMutation.isPending}
            getOptionLabel={formatSymbolLabel}
            onInputChange={(_, value) => {
              setSymbolSearch(value);
              setSymbolReplacement((current) => ({ ...current, newSymbol: value }));
            }}
            onChange={(_, value) => {
              const nextSymbol = typeof value === 'string' ? value : value?.symbol || '';
              setSymbolReplacement((current) => ({ ...current, newSymbol: nextSymbol }));
              setSymbolSearch(nextSymbol);
            }}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                {formatSymbolLabel(option)}
              </Box>
            )}
            renderInput={(params) => <TextField {...params} label="New symbol" />}
          />
          <TextField
            label="Old symbol (optional)"
            size="small"
            value={symbolReplacement.oldSymbol}
            disabled={bulkReplaceMutation.isPending}
            onChange={(event) => setSymbolReplacement((current) => ({
              ...current,
              oldSymbol: event.target.value,
            }))}
            sx={{ minWidth: { sm: 220 } }}
          />
        </Stack>
      </Box>
    </Stack>
  );

  const renderReplacementRows = (
    rows: ReplacementRow[],
    setter: React.Dispatch<React.SetStateAction<ReplacementRow[]>>,
    disabled = false,
  ) => (
    <Stack spacing={1.25}>
      {rows.map((row) => (
        <Stack key={row.id} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <TextField
            label="Find"
            size="small"
            value={row.from}
            disabled={disabled}
            onChange={(event) => updateReplacementRow(setter, row.id, { from: event.target.value })}
            sx={{ flex: 1 }}
          />
          <TextField
            label="Replace"
            size="small"
            value={row.to}
            disabled={disabled}
            onChange={(event) => updateReplacementRow(setter, row.id, { to: event.target.value })}
            sx={{ flex: 1 }}
          />
          <FormControlLabel
            control={(
              <Checkbox
                size="small"
                checked={row.case_sensitive !== false}
                disabled={disabled}
                onChange={(event) => updateReplacementRow(setter, row.id, { case_sensitive: event.target.checked })}
              />
            )}
            label="Case"
            sx={{ mx: 0, minWidth: 86 }}
          />
          <IconButton
            size="small"
            onClick={() => removeReplacementRow(setter, row.id)}
            disabled={disabled}
            title="Remove replacement"
          >
            <RemoveCircleOutlineIcon fontSize="small" />
          </IconButton>
        </Stack>
      ))}
      <Button
        variant="outlined"
        size="small"
        startIcon={<AddCircleOutlineIcon />}
        onClick={() => setter((rows) => [...rows, createReplacementRow()])}
        disabled={disabled}
        sx={{ alignSelf: 'flex-start' }}
      >
        Add replacement
      </Button>
    </Stack>
  );

  // Handle file upload
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setImportData(content);
        
        // Try to parse and extract provider info for API key mapping
        try {
          const parsedData = JSON.parse(content);
          if (parsedData.llm_providers) {
            const mappings: Record<string, string> = {};
            parsedData.llm_providers.forEach((provider: any) => {
              if (provider.api_key === '***MASKED***') {
                mappings[provider.export_id] = '';
              }
            });
            setApiKeyMappings(mappings);
          }
        } catch (error) {
          console.error('Failed to parse import data:', error);
        }
      };
      reader.readAsText(file);
    }
  };

  return (
    <Box>
      <PageHeader
        title="AI Systems"
        actions={
          <Box display="flex" gap={{ xs: 0.5, sm: 1 }} flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={<ImportIcon />}
              onClick={() => setImportDialogOpen(true)}
              size="small"
              sx={{ 
                display: { xs: 'none', sm: 'flex' },
              }}
            >
              Import System
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setWizardOpen(true)}
              size="small"
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Create System</Box>
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>New</Box>
            </Button>
          </Box>
        }
      />

      <AISystemFormAdvanced
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditingSystem(undefined);
        }}
        system={editingSystem}
      />

      <CreateSystemWizard
        open={wizardOpen}
        onClose={closeCreateWizard}
      />

      <ConfirmDialog
        open={!!deletingSystem}
        title="Delete AI System"
        message={`Are you sure you want to delete "${deletingSystem?.name}"? The system and its associated experts, shared inputs, and schedules will be disabled and hidden, while their data and execution history are retained.`}
        onConfirm={async () => {
          if (deletingSystem) {
            await deleteMutation.mutateAsync(deletingSystem.id);
          }
        }}
        onCancel={() => setDeletingSystem(null)}
        confirmText="Delete"
        confirmColor="error"
        loading={deleteMutation.isPending}
      />

      {isLoading ? (
        <LoadingSpinner message="Loading systems..." />
      ) : (
        <>
          <Paper variant="outlined" sx={{ p: { xs: 1.25, sm: 1.5 }, mb: { xs: 2, sm: 3 } }}>
            <Stack
              direction={{ xs: 'column', lg: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', lg: 'center' }}
              gap={1.25}
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                <TextField
                  placeholder="Search systems"
                  variant="outlined"
                  size="small"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{ width: { xs: '100%', sm: 280, md: 320 } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />

                <ToggleButtonGroup
                  size="small"
                  value={statusFilter}
                  exclusive
                  onChange={(_, value) => {
                    if (value) {
                      setStatusFilter(value);
                    }
                  }}
                  sx={{ flexWrap: 'wrap' }}
                >
                  <ToggleButton value="all" sx={{ textTransform: 'none' }}>All</ToggleButton>
                  <ToggleButton value="active" sx={{ textTransform: 'none' }}>Active</ToggleButton>
                  <ToggleButton value="inactive" sx={{ textTransform: 'none' }}>Inactive</ToggleButton>
                </ToggleButtonGroup>

                <ToggleButtonGroup
                  size="small"
                  value={templateFilter}
                  exclusive
                  onChange={(_, value) => {
                    if (value) {
                      setTemplateFilter(value);
                    }
                  }}
                  sx={{ flexWrap: 'wrap' }}
                >
                  <ToggleButton value="all" sx={{ textTransform: 'none' }}>All</ToggleButton>
                  <ToggleButton value="systems" sx={{ textTransform: 'none' }}>Systems</ToggleButton>
                  <ToggleButton value="templates" sx={{ textTransform: 'none' }}>Templates</ToggleButton>
                </ToggleButtonGroup>

                <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 170 } }}>
                  <InputLabel id="systems-sort-label">Sort</InputLabel>
                  <Select
                    labelId="systems-sort-label"
                    label="Sort"
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value as SystemSortOrder)}
                  >
                    <MenuItem value="-created_at">Newest first</MenuItem>
                    <MenuItem value="created_at">Oldest first</MenuItem>
                    <MenuItem value="-week_cost">Highest weekly cost</MenuItem>
                    <MenuItem value="week_cost">Lowest weekly cost</MenuItem>
                  </Select>
                </FormControl>
              </Stack>

              <Typography variant="body2" color="text.secondary">
                {data?.count ?? 0} system{(data?.count ?? 0) === 1 ? '' : 's'}
              </Typography>
            </Stack>
          </Paper>

          {/* Systems Grid */}
          <Grid container spacing={{ xs: 2.25, sm: 3.25, md: 4.25 }}>
            {data?.results.map((system: AISystemListItem) => {
              const isOwner = system.user === user?.id;
              return (
              <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={system.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', border: '1px solid', borderColor: 'divider' }}>
                  <CardContent sx={{ p: { xs: 1.125, sm: 1.5 }, display: 'flex', flexDirection: 'column', gap: 1, flexGrow: 1 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                      <Box minWidth={0}>
                        <Chip
                          label={system.is_active ? 'Active' : 'Inactive'}
                          color={system.is_active ? 'success' : 'default'}
                          size="small"
                          sx={{ mb: 0.5 }}
                        />
                        {system.is_template && (
                          <Chip
                            label="Template"
                            color="secondary"
                            size="small"
                            variant="outlined"
                            sx={{ mb: 0.5, ml: 0.5 }}
                          />
                        )}
                        {system.is_template && system.is_public && (
                          <Chip
                            label="Public"
                            color="info"
                            size="small"
                            variant="outlined"
                            sx={{ mb: 0.5, ml: 0.5 }}
                          />
                        )}
                        <Typography
                          variant="h6"
                          component="div"
                          sx={{
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            fontWeight: 700,
                            lineHeight: 1.25,
                            wordBreak: 'break-word',
                          }}
                        >
                          {system.name}
                        </Typography>
                      </Box>

                      <Switch
                        checked={system.is_active}
                        onChange={(e) => handleToggleActive(system, e.target.checked)}
                        disabled={toggleActiveMutation.isPending || !isOwner}
                        inputProps={{ 'aria-label': `Toggle ${system.name}` }}
                      />
                    </Box>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        minHeight: 44,
                        fontSize: { xs: '0.82rem', sm: '0.875rem' },
                      }}
                    >
                      {system.description || 'No description added yet.'}
                    </Typography>

                    {!!system.tags?.length && (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ rowGap: 0.5 }}>
                        {system.tags.slice(0, 5).map((tag) => (
                          <Chip
                            key={tag}
                            label={tag}
                            size="small"
                            variant="outlined"
                            sx={{ maxWidth: '100%' }}
                          />
                        ))}
                      </Stack>
                    )}

                    <Grid container spacing={0.75}>
                      <Grid size={{ xs: 6 }}>
                        <Paper variant="outlined" sx={{ px: 1, py: 0.75 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Today's Cost
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {formatCurrency(system.today_cost)}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Paper variant="outlined" sx={{ px: 1, py: 0.75 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            This Week
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {formatCurrency(system.week_cost)}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Paper variant="outlined" sx={{ px: 1, py: 0.75 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            Last Run
                          </Typography>
                          <Chip
                            size="small"
                            color={runStatusColor(system.last_run_status)}
                            label={formatRunStatus(system.last_run_status)}
                          />
                        </Paper>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Paper variant="outlined" sx={{ px: 1, py: 0.75 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Next Schedule
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formatDateTime(system.next_schedule_at)}
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>

                    <Typography variant="caption" color="text.secondary">
                      Created {formatCreatedDate(system.created_at)}
                      {system.last_run_at ? ` • Last activity ${formatDateTime(system.last_run_at)}` : ''}
                    </Typography>
                  </CardContent>

                  <CardActions sx={{ px: { xs: 1.125, sm: 1.5 }, pb: { xs: 1.125, sm: 1.5 }, pt: 0, justifyContent: 'space-between', gap: 0.625, flexWrap: 'wrap' }}>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<ViewIcon fontSize="small" />}
                        onClick={() => navigate(`/ai/systems/${system.id}`)}
                        disabled={!isOwner}
                      >
                        Open System
                      </Button>
                      {system.is_template && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<CloneIcon fontSize="small" />}
                          onClick={() => handleClone(system)}
                          disabled={cloneMutation.isPending}
                        >
                          Use Template
                        </Button>
                      )}
                    </Stack>

                    <Stack direction="row" spacing={0.5}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleExport(system)}
                        disabled={!isOwner}
                        title="Export system"
                      >
                        <ExportIcon fontSize="small" />
                      </IconButton>

                      {isOwner && !system.is_template && (
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() => handleClone(system)}
                          disabled={cloneMutation.isPending}
                          title="Clone system"
                        >
                          <CloneIcon fontSize="small" />
                        </IconButton>
                      )}

                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenBulkReplace(system)}
                        disabled={bulkReplaceMutation.isPending || !isOwner}
                        title="Bulk replace"
                      >
                        <FindReplaceIcon fontSize="small" />
                      </IconButton>

                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeletingSystem(system)}
                        disabled={deleteMutation.isPending || !isOwner}
                        title="Delete system"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </CardActions>
                </Card>
              </Grid>
              );
            })}
          </Grid>

          {data?.results.length === 0 && (
            <Box textAlign="center" py={{ xs: 4, sm: 8 }}>
              <Typography color="text.secondary">
                No systems found. Create your first AI system to get started!
              </Typography>
            </Box>
          )}
        </>
      )}

      {/* Import Dialog */}
      <Dialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={window.innerWidth < 600}
      >
        <DialogTitle>Import AI System</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Upload a JSON file exported from another AI system. If the export contains
              masked API keys, you'll need to provide the real keys below.
            </Alert>

            <Button
              variant="outlined"
              component="label"
              fullWidth
              sx={{ mb: 3 }}
            >
              Choose File
              <input
                type="file"
                hidden
                accept=".json"
                onChange={handleFileUpload}
              />
            </Button>

            <TextField
              label="Import Data (JSON)"
              multiline
              rows={10}
              fullWidth
              value={importData}
              onChange={(e) => setImportData(e.target.value)}
              placeholder="Paste JSON export data or upload a file..."
              sx={{ mb: 3 }}
            />

            {Object.keys(apiKeyMappings).length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  API Key Mappings (Required for masked providers)
                </Typography>
                {Object.entries(apiKeyMappings).map(([providerId, key]) => (
                  <TextField
                    key={providerId}
                    label={`API Key for ${providerId}`}
                    fullWidth
                    type="password"
                    value={key}
                    onChange={(e) =>
                      setApiKeyMappings({
                        ...apiKeyMappings,
                        [providerId]: e.target.value,
                      })
                    }
                    sx={{ mb: 2 }}
                    helperText="Leave empty to create inactive provider with masked key"
                  />
                ))}
              </Box>
            )}

            <Divider sx={{ my: 2.5 }} />
            <Typography variant="subtitle2" gutterBottom>
              Bulk replacements
            </Typography>
            {renderReplacementRows(importReplacementRows, setImportReplacementRows, importMutation.isPending)}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={!importData || importMutation.isPending}
          >
            {importMutation.isPending ? 'Importing...' : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={bulkReplaceDialogOpen}
        onClose={() => setBulkReplaceDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={window.innerWidth < 600}
      >
        <DialogTitle>Bulk Replace</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {bulkReplaceSystem?.name}
            </Typography>
            {renderScopedReplacementControls()}
            <Divider sx={{ my: 2.5 }} />
            <Typography variant="subtitle2" gutterBottom>
              Custom replacements
            </Typography>
            {renderReplacementRows(bulkReplacementRows, setBulkReplacementRows, bulkReplaceMutation.isPending)}
            <FormControlLabel
              sx={{ mt: 2 }}
              control={(
                <Checkbox
                  checked={bulkReplaceIncludeAgents}
                  onChange={(event) => setBulkReplaceIncludeAgents(event.target.checked)}
                  disabled={bulkReplaceMutation.isPending}
                />
              )}
              label="Include linked agents"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkReplaceDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleBulkReplace}
            disabled={bulkReplaceMutation.isPending}
          >
            {bulkReplaceMutation.isPending ? 'Applying...' : 'Apply'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AISystemList;
